import { uid } from 'quasar';
import { noteAuditorAnalytics } from 'src/services/analytics';

import { LEARNING_STATUSES } from '../../domain/constants/auditLearnings';

/** @typedef {import('../../domain/entities/AuditLearning.types').AuditLearning} AuditLearning */

// eslint-disable-next-line import/prefer-default-export
export function useLearningsAnalytics() {
  const {
    LEARNING_DELETE_VARIANTS,
    LEARNING_EDIT_MODES,
    LEARNING_SAVE_INTENTS,
    LEARNING_SCOPE_CHANGES,
    LEARNING_SCOPES,
    LEARNING_STATUS_CHANGE_CAUSES,
    LEARNING_STATUSES: ANALYTICS_STATUSES,
    LEARNING_VALIDATION_OUTCOMES,
  } = noteAuditorAnalytics;

  /**
   * @param {string[]} [templateIds]
   * @returns {string}
   */
  function describeScope(templateIds) {
    return templateIds?.length
      ? LEARNING_SCOPES.TEMPLATES
      : LEARNING_SCOPES.ORGANIZATION;
  }

  /**
   * @param {AuditLearning} learning
   * @returns {string}
   */
  function describeStatus(learning) {
    if (learning.status === LEARNING_STATUSES.DRAFT) {
      return learning.parentId ? ANALYTICS_STATUSES.REVISION_DRAFT : ANALYTICS_STATUSES.DRAFT;
    }

    return learning.draftRevisionId
      ? ANALYTICS_STATUSES.ACTIVE_WITH_DRAFT
      : ANALYTICS_STATUSES.ACTIVE;
  }

  /**
   * @param {AuditLearning} learning
   * @returns {string}
   */
  function describeDeleteVariant(learning) {
    if (learning.status === LEARNING_STATUSES.DRAFT) {
      return learning.parentId
        ? LEARNING_DELETE_VARIANTS.REVISION_DRAFT
        : LEARNING_DELETE_VARIANTS.STANDALONE_DRAFT;
    }

    return learning.draftRevisionId
      ? LEARNING_DELETE_VARIANTS.ACTIVE_WITH_DRAFT
      : LEARNING_DELETE_VARIANTS.ACTIVE_NO_DRAFT;
  }

  /**
   * @param {string[]} [before]
   * @param {string[]} [after]
   * @returns {string|null}
   */
  function describeScopeChange(before, after) {
    const wasTemplates = describeScope(before) === LEARNING_SCOPES.TEMPLATES;
    const isTemplates = describeScope(after) === LEARNING_SCOPES.TEMPLATES;

    if (wasTemplates && !isTemplates) {
      return LEARNING_SCOPE_CHANGES.TO_ORGANIZATION;
    }

    if (!wasTemplates && isTemplates) {
      return LEARNING_SCOPE_CHANGES.TO_TEMPLATES;
    }

    if (!wasTemplates && !isTemplates) {
      return null;
    }

    const sortedBefore = [...before].sort();
    const sortedAfter = [...after].sort();

    const sameTemplates = sortedBefore.length === sortedAfter.length
      && sortedBefore.every((id, index) => id === sortedAfter[index]);

    return sameTemplates ? null : LEARNING_SCOPE_CHANGES.TEMPLATES_CHANGED;
  }

  /**
   * @param {AuditLearning} learning
   * @returns {number|null}
   */
  function draftAgeInMinutes(learning) {
    const created = learning?.createTimestamp;

    const createdMs = (() => {
      if (created && typeof created.toMillis === 'function') return created.toMillis();
      if (created instanceof Date) return created.getTime();
      if (typeof created === 'number') return created;
      return null;
    })();

    if (createdMs === null) {
      return null;
    }

    return Math.max(0, Math.round((Date.now() - createdMs) / 60000));
  }

  /**
   * @param {Object} params
   * @param {AuditLearning|null} [params.learning] - The learning being edited, absent on a create.
   * @param {number} [params.totalLearnings]
   * @returns {{ learningEditAttemptId: string, mode: string }}
   */
  function trackEditStarted({ learning = null, totalLearnings = 0 } = {}) {
    const learningEditAttemptId = uid();

    if (!learning) {
      noteAuditorAnalytics.trackLearningCreationStarted({ learningEditAttemptId, totalLearnings });

      return { learningEditAttemptId, mode: LEARNING_EDIT_MODES.CREATE };
    }

    noteAuditorAnalytics.trackLearningEditOpened({
      learningEditAttemptId,
      learningId: learning.id,
      source: learning.source ?? null,
      status: describeStatus(learning),
      scope: describeScope(learning.sessionNoteTemplateIds),
      templateCount: learning.sessionNoteTemplateIds?.length ?? 0,
    });

    return { learningEditAttemptId, mode: LEARNING_EDIT_MODES.EDIT };
  }

  return {
    LEARNING_EDIT_MODES,
    LEARNING_SAVE_INTENTS,
    LEARNING_STATUS_CHANGE_CAUSES,
    LEARNING_STATUSES: ANALYTICS_STATUSES,
    LEARNING_VALIDATION_OUTCOMES,
    describeDeleteVariant,
    describeScope,
    describeScopeChange,
    describeStatus,
    draftAgeInMinutes,
    trackEditStarted,
    trackEditAbandoned: noteAuditorAnalytics.trackLearningEditAbandoned
      .bind(noteAuditorAnalytics),
    trackLearningDeleted: noteAuditorAnalytics.trackLearningDeleted
      .bind(noteAuditorAnalytics),
    trackLearningSaved: noteAuditorAnalytics.trackLearningSaved
      .bind(noteAuditorAnalytics),
    trackRevisionRestored: noteAuditorAnalytics.trackLearningRevisionRestored
      .bind(noteAuditorAnalytics),
    trackStatusChanged: noteAuditorAnalytics.trackLearningStatusChanged
      .bind(noteAuditorAnalytics),
    trackValidationCompleted: noteAuditorAnalytics.trackLearningValidationCompleted
      .bind(noteAuditorAnalytics),
  };
}
